import React, {
	useCallback,
	useEffect,
	useRef,
	useMemo,
	memo,
	type ReactNode,
	type PropsWithChildren,
} from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import {
	SpotlightTarget as AkSpotlightTarget,
	// @ts-expect-error - TS2305 - Module '"@atlaskit/onboarding"' has no exported member 'SpotlightTargetProps'.
	type SpotlightTargetProps,
} from '@atlaskit/onboarding';
import { ARTICLE_TYPE } from '@atlaskit/help';
import { useIsSimplifiedProject } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	testIdConcat,
	testIdGenerate,
} from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import {
	useRightSidebarController,
	useActiveRightSidebarState as useRightSidebarState,
} from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useTopMenus } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	HELP_PANEL_ID,
	HELP_PANEL_WIDTH,
	HELP_PANEL_VERSION,
} from '@atlassian/jira-help-panel-common-constant/src/index.tsx';
import { useHelpPanelActions } from '@atlassian/jira-help-panel-services/src/components/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { QuickstartNudge } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/quickstart-nudge/main.tsx';
import {
	useIsDismissed,
	useOpenTask,
	useQuickstartActions,
} from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { FeedbackNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/jwm-feedback/async.tsx';
import { useHasOpened } from '@atlassian/jira-software-onboarding-quickstart-has-opened/src/services/has-opened/index.tsx';
import { JWM_FEEDBACK_TASK } from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';
import { useHelpButtonState } from '../../controllers/help-button/index.tsx';
import { Help as Trigger } from '../../common/ui/help-button/index.tsx';
import { NAVIGATION_ITEM_ID, HELP_SPOTLIGHT_KEY } from '../../common/constants.tsx';
import { useNavigationItemAnalytics } from '../../controllers/navigation-item-analytics/main.tsx';
import { TopLevelErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import messages from './messages.tsx';
import { Badge } from './badge/index.tsx';

export const SpotlightTargetComponent = componentWithCondition(
	() => fg('blu-5788-spotlight-help-button'),
	JWMFeedbackSpotlightWrapper,
	SpotlightTargetComponentOriginal,
);

function SpotlightTargetComponentOriginal({ children }: { children: ReactNode }) {
	return __SERVER__ && !fg('jfp-magma-4013-fix-top-nav-ssr-hydration') ? (
		<>{children}</>
	) : (
		<>
			<SpotlightTarget name={HELP_SPOTLIGHT_KEY}>{children}</SpotlightTarget>
			<EngagementSpotlight name={HELP_SPOTLIGHT_KEY} />
		</>
	);
}

function SpotlightTarget({ children, ...props }: SpotlightTargetProps) {
	return (
		<AkSpotlightTarget {...props}>
			{/*
			 * The atlaskit spotlight target component merely registers a ref on the child component,
			 *  so there is no way to give it a test id. We want to keep the test id as closely coupled
			 *  with the target as possible, so we add a span with id here, rather than on the child
			 *  where it could become more easily uncoupled.
			 */}
			<div data-test-id={`ak-spotlight-target-${props.name}`}>{children}</div>
		</AkSpotlightTarget>
	);
}

function JWMFeedbackSpotlightWrapper({ children }: PropsWithChildren) {
	const renderNudgeWrapper = useCallback(
		(kids: ReactNode) => <FeedbackNudgeAsync>{kids}</FeedbackNudgeAsync>,
		[],
	);
	const [{ hasOpened: shouldShowNudge }] = useHasOpened();
	const [isUnloaded] = useIsDismissed();
	const [openTask] = useOpenTask();
	const [, { setHasNudgeActive }] = useQuickstartActions();

	useEffect(() => {
		if (!isUnloaded && openTask === JWM_FEEDBACK_TASK && shouldShowNudge) {
			setHasNudgeActive(true);
		}
	}, [isUnloaded, openTask, shouldShowNudge, setHasNudgeActive]);

	return !__SERVER__ && shouldShowNudge ? (
		<QuickstartNudge renderWrapper={renderNudgeWrapper}>{children}</QuickstartNudge>
	) : (
		<SpotlightTargetComponentOriginal>{children}</SpotlightTargetComponentOriginal>
	);
}

const Help = () => {
	const isProjectSimplified = useIsSimplifiedProject();
	const { formatMessage } = useIntl();
	const [isMenuOpen, { off: closeMenu, on: openMenu }] = useTopMenus(MENU_ID.HELP);
	const isAnonymous = useIsAnonymous();
	const [topMenusValue, { off: closeAllMenus }] = useTopMenus();
	const [, { setNavigationData }] = useHelpPanelActions();
	const { releaseNotesNotifications, setReleaseNotesNotifications } = useHelpButtonState();

	const helpButtonRef = useRef<HTMLButtonElement>(null);

	const attributes = useMemo(
		() => ({
			helpPanelVersion: HELP_PANEL_VERSION,
			helpPanelType: 'inProductHelp',
			nextGenProject: isProjectSimplified,
		}),
		[isProjectSimplified],
	);

	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.HELP,
	});

	const { openRightSidebar, closeCurrentRightSidebar } = useRightSidebarController();
	const currentRightSidebarState = useRightSidebarState();
	const isHelpPanelOpenedPreviously = usePrevious(currentRightSidebarState);
	const testIdPrefix = testIdGenerate('secondary-actions', 'help');

	const onClick = useCallback(() => {
		if (
			currentRightSidebarState &&
			currentRightSidebarState.panelId === HELP_PANEL_ID &&
			!currentRightSidebarState.isCollapsed
		) {
			closeCurrentRightSidebar();
		} else {
			openRightSidebar(HELP_PANEL_ID, HELP_PANEL_WIDTH, false, true);
		}

		closeAllMenus();
		setNavigationData({ articleId: { id: '', type: ARTICLE_TYPE.HELP_ARTICLE }, history: [] });

		// Shows if the help panel is currently open or closed
		const isPanelOpen =
			currentRightSidebarState?.panelId === HELP_PANEL_ID && !currentRightSidebarState?.isCollapsed;

		triggerAnalytics({
			...attributes,
			isOpened: !isPanelOpen,
		});
	}, [
		triggerAnalytics,
		attributes,
		currentRightSidebarState,
		closeAllMenus,
		setNavigationData,
		closeCurrentRightSidebar,
		openRightSidebar,
	]);

	const badge = useCallback(() => {
		if (isAnonymous) {
			return null;
		}

		return <Badge releaseNotesNotifications={releaseNotesNotifications ?? 0} />;
	}, [isAnonymous, releaseNotesNotifications]);

	// Restore the notifications count when the help panel is opened and the notifications count is not 0
	// We explicitly check for the collapsed state, as currentRightSidebarState is null when the sidebar is closed
	// and we don't want to set the notifications count to 0 in that case
	const shouldRestoreNotificationsCount =
		currentRightSidebarState?.isCollapsed === false && releaseNotesNotifications !== 0;

	useEffect(() => {
		if (shouldRestoreNotificationsCount) {
			setReleaseNotesNotifications(0);
		}
	}, [setReleaseNotesNotifications, shouldRestoreNotificationsCount]);

	useEffect(() => {
		if (
			currentRightSidebarState?.panelId === HELP_PANEL_ID &&
			!currentRightSidebarState?.isCollapsed
		) {
			openMenu();
		} else {
			closeMenu();
			if (isHelpPanelOpenedPreviously?.panelId === HELP_PANEL_ID && !currentRightSidebarState) {
				requestAnimationFrame(() => helpButtonRef.current?.focus());
			}
		}
	}, [closeMenu, currentRightSidebarState, isHelpPanelOpenedPreviously, openMenu, topMenusValue]);

	const dataVc =
		__SERVER__ && !fg('jfp-magma-4013-fix-top-nav-ssr-hydration')
			? 'atlassian-navigation-help-ssr'
			: 'atlassian-navigation-help';
	return (
		<div data-testid={testIdConcat(testIdPrefix, 'menu-trigger')} data-vc={dataVc}>
			<SpotlightTargetComponent>
				<Trigger
					aria-expanded={false}
					aria-haspopup={false}
					isSelected={isMenuOpen}
					badge={badge}
					onClick={onClick}
					tooltip={formatMessage(messages.tooltip)}
					label={formatMessage(messages.label)}
					ref={helpButtonRef}
				/>
			</SpotlightTargetComponent>
		</div>
	);
};

const HelpWithErrorBoundary = memo(() => (
	<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.HELP}>
		<Help />
	</TopLevelErrorBoundary>
));

export { HelpWithErrorBoundary as Help };
